/**
 * Signup Progress Indicator Component
 *
 * Visual step indicator showing progress through the 5-stage guide signup flow.
 * SCRUM-1853: Extended from 3 to 5 steps.
 *
 * Epic: MVP 1.2.1.2 - Guide Signup Flow
 * Location: src/components/guide/signup/SignupProgress.jsx
 */

import React, { Fragment } from 'react';

const STEPS = [
  'Basic Info',
  'Verify Identity',
  'Service Details',
  'Upload Documents',
  'Complete',
];

/**
 * SignupProgress Component
 *
 * @param {Object} props
 * @param {number} props.currentStep - Current step (1-5)
 * @param {string} [props.className] - Additional CSS classes
 */
const SignupProgress = ({ currentStep = 1, className = '' }) => {
  const getStatus = (stepNumber) => {
    if (stepNumber < currentStep) { return 'completed'; }
    if (stepNumber === currentStep) { return 'current'; }
    return 'upcoming';
  };

  const getStyles = (status) => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-emerald-600 border-emerald-600',
          text: 'text-emerald-700',
          line: 'bg-emerald-600',
        };
      case 'current':
        return {
          circle: 'bg-white border-emerald-600 ring-4 ring-emerald-100',
          text: 'text-emerald-700 font-semibold',
          line: 'bg-gray-300',
        };
      default:
        return {
          circle: 'bg-white border-gray-300',
          text: 'text-gray-500',
          line: 'bg-gray-300',
        };
    }
  };

  return (
    <div className={className}>
      <nav aria-label="Signup progress">
        <ol className="flex items-start">
          {STEPS.map((label, index) => {
            const stepNumber = index + 1;
            const status = getStatus(stepNumber);
            const styles = getStyles(status);
            const isLast = index === STEPS.length - 1;

            return (
              <Fragment key={stepNumber}>
                <li className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${styles.circle}`}
                  >
                    {status === 'completed' ? (
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span className={`text-sm font-semibold ${status === 'current' ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {stepNumber}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 text-center">
                    <p className={`text-sm ${styles.text} transition-colors duration-300`}>
                      {label}
                    </p>
                  </div>
                </li>

                {!isLast && (
                  <li role="presentation" aria-hidden="true" className="flex-1 px-2 mt-5">
                    <div className={`h-0.5 w-full transition-colors duration-300 ${styles.line}`} />
                  </li>
                )}
              </Fragment>
            );
          })}
        </ol>
      </nav>

      <div className="mt-4 sm:hidden">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>Step {currentStep} of {STEPS.length}</span>
          <span>{Math.round(((currentStep - 1) / STEPS.length) * 100)}% Complete</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-600 transition-all duration-300"
            style={{ width: `${((currentStep - 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default SignupProgress;
